import { useAuth } from '@milliswift/auth'
import { APP_URLS } from '@milliswift/api-client'
import { Badge, Callout, CopyField, Field, FormSection, Input } from '@milliswift/ui'
import { KeyRound, Mail } from 'lucide-react'

/**
 * Profile.
 *
 * The platform does not expose a profile-update endpoint yet (there is no
 * PUT /auth/me), and the password-change flow runs through the reset pipeline
 * rather than a direct change call. Rather than wire a Save button to a
 * mutation that cannot succeed — which would be a button that lies — this page
 * presents the data read-only with pointers to the flows that do exist.
 *
 * When PUT /auth/me lands, the sections below already have the right shape.
 */
export default function Profile() {
  const { user } = useAuth()

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold tracking-tight text-foreground">Profile</h1>
        <p className="mt-1 text-sm text-muted">Your identity on this account.</p>
      </div>

      <FormSection title="Personal details" description="Shown to other members of your organisation.">
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Full name">
            <Input type="text" value={user?.full_name ?? ''} readOnly disabled />
          </Field>

          <Field label="Company">
            <Input
              type="text"
              value={user?.company_name ?? ''}
              readOnly
              disabled
              placeholder="—"
            />
          </Field>
        </div>

        <Field label="Email" hint="Your email is your sign-in. Changing it is not available yet.">
          <Input
            type="email"
            value={user?.email ?? ''}
            readOnly
            disabled
            leading={<Mail className="h-4 w-4" />}
          />
        </Field>

        <Callout tone="info">
          Editing your details requires an update endpoint the platform has not
          shipped yet. Your account ID and role are always current below.
        </Callout>
      </FormSection>

      <FormSection title="Password" description="Changes run through the reset flow.">
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Password" hint="Set via the reset flow: request a link, then choose a new password.">
            <Input
              type="password"
              value="••••••••••••"
              readOnly
              disabled
              leading={<KeyRound className="h-4 w-4" />}
            />
          </Field>

          <div className="flex items-end">
            <a
              href={`${APP_URLS.accounts}/forgot-password`}
              className="text-sm font-medium text-accent underline-offset-4 hover:underline"
            >
              Request a password reset →
            </a>
          </div>
        </div>
      </FormSection>

      <FormSection title="Account" description="Identifiers you may need for support.">
        <div className="grid gap-5 sm:grid-cols-2">
          <CopyField
            label="User ID"
            value={user?.id ?? ''}
            hint="Safe to share — it is not a secret."
          />
          <div className="space-y-2">
            <p className="text-caption font-medium text-foreground">Two-factor auth</p>
            <Badge tone={user?.mfa_enabled ? 'success' : 'warning'} dot>
              {user?.mfa_enabled ? 'Enabled' : 'Not enabled'}
            </Badge>
            <p className="text-caption text-muted">
              {user?.mfa_enabled
                ? 'Manage it from the Security page.'
                : 'Strongly recommended — enable it from the Security page.'}
            </p>
          </div>
        </div>
      </FormSection>
    </div>
  )
}
